// frontend/src/components/Auth/Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../../api/client';
import './Login.css';

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [rememberMe, setRememberMe] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const usernameInputRef = useRef(null);
    const cardRef = useRef(null);

    useEffect(() => {
        if (usernameInputRef.current) {
            usernameInputRef.current.focus();
        }
    }, []);

    const handleChange = (e) => {
        const { id, value } = e.target;
        if (id === 'usernameInput') {
            setFormData({ ...formData, username: value.toLowerCase() });
        } else if (id === 'passwordInput') {
            setFormData({ ...formData, password: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const { username, password } = formData;
        
        if (!username || !password) {
            setError('⚔️ Enter username & password! ⚔️');
            triggerShake();
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authAPI.login({ username, password });
            
            if (response.success) {
                if (cardRef.current) {
                    cardRef.current.classList.add('victory-glow');
                }
                setTimeout(() => {
                    navigate('/concept-selection');
                }, 1000);
            } else {
                setError('❌ Invalid credentials! ❌');
                triggerShake();
            }
        } catch (err) {
            setError('💀 Login failed! Try again. 💀');
            triggerShake();
        } finally {
            setLoading(false);
        }
    };

    const triggerShake = () => {
        if (cardRef.current) {
            cardRef.current.classList.add('game-shake');
            setTimeout(() => {
                cardRef.current.classList.remove('game-shake');
            }, 400);
        }
    };

    const togglePassword = () => {
        setShowPassword(!showPassword);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    return (
        <div className="gamified-login-world">
            {/* Simple floating particles only - no coins */}
            <div className="particle-bg">
                {[...Array(20)].map((_, i) => (
                    <div key={i} className="particle" style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 10}s`,
                        animationDuration: `${8 + Math.random() * 7}s`
                    }}></div>
                ))}
            </div>

            <div className="login-container">
                <div className="row justify-content-center">
                    <div className="col-lg-5 col-md-7">
                        <div ref={cardRef} className="game-card">
                            {/* Header */}
                            <div className="card-header-gamified">
                                <div className="game-badge">
                                    <i className="fas fa-dice-d20"></i>
                                    <span>MATH QUEST</span>
                                    <i className="fas fa-dice-d6"></i>
                                </div>
                                <h2>⚔️ Hero's Login ⚔️</h2>
                                <p>Enter the realm of numbers</p>
                            </div>
                            
                            {/* Body */}
                            <div className="card-body-gamified">
                                {error && (
                                    <div className="game-alert">
                                        <i className="fas fa-skull me-2"></i>
                                        {error}
                                    </div>
                                )}
                                
                                <form onSubmit={handleSubmit}>
                                    {/* Username */}
                                    <div className="input-group-game">
                                        <label>
                                            <i className="fas fa-user-ninja"></i> Username
                                        </label>
                                        <input 
                                            type="text" 
                                            id="usernameInput" 
                                            ref={usernameInputRef}
                                            placeholder="Enter your hero name"
                                            autoComplete="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    
                                    {/* Password */}
                                    <div className="input-group-game">
                                        <label>
                                            <i className="fas fa-shield-haltered"></i> Password
                                        </label>
                                        <div className="password-wrapper">
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                id="passwordInput" 
                                                placeholder="Enter your secret"
                                                autoComplete="current-password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                onKeyPress={handleKeyPress}
                                                required
                                            />
                                            <button type="button" onClick={togglePassword}>
                                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Options */}
                                    <div className="options-row">
                                        <label className="checkbox-game">
                                            <input 
                                                type="checkbox" 
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                            />
                                            <span>Remember me</span>
                                        </label>
                                        <Link to="/forgot-password" className="forgot-link">
                                            Forgot password?
                                        </Link>
                                    </div>
                                    
                                    {/* Login Button */}
                                    <button type="submit" className="login-btn-game" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <span className="spinner"></span>
                                                Logging in...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-gamepad"></i>
                                                Start Adventure
                                                <i className="fas fa-crown"></i>
                                            </>
                                        )}
                                    </button>
                                    
                                    {/* Signup */}
                                    <div className="signup-link">
                                        New here? 
                                        <Link to="/username">
                                            <strong> Create Hero</strong> →
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;